import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

// Sprint ACAD ACT.A6 (#1707) — student profile page, Certificates section.
//
// No student-facing profile page existed before this story (confirmed in
// recon — Sprint-Artefacts/Sprint-ACT.A6-recon.md §5). This is new,
// minimal scaffolding: a single-section page (no tabs needed yet, since
// Certificates is the only content), following DecisionsPage.tsx's
// fetch/loading/error/list shape and this app's inline-CSS-variable
// styling convention.

interface CertificateListItem {
  certificateId:   string;
  projectId:       string;
  applicationName: string;
  institutionName: string | null;
  goLiveDate:      string | null;
  issuedAt:        string;
  pdfUrl:          string;
  verifyUrl:       string;
}

// #1804 (Phase 1) — self-service confirm-name surface. Certificate
// generation is instant and fire-and-forget the moment a project's phase 8
// completes (no pause/review step anywhere in that chain), so this can't be
// a blocking gate — it's a standing field the student can set any time
// before their project gets there. Defaults to their signup name until
// they explicitly confirm/correct it.
interface CertDisplayNameResponse {
  name:              string;
  cert_display_name: string | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

async function shareCertificate(cert: CertificateListItem) {
  const shareData = {
    title: `BluePrint SDLC Certificate — ${cert.applicationName}`,
    text: `Verify my BluePrint SDLC Certificate for ${cert.applicationName}`,
    url: cert.verifyUrl,
  };
  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return;
    } catch {
      // User cancelled the share sheet — fall through to clipboard copy.
    }
  }
  await navigator.clipboard.writeText(cert.verifyUrl);
  window.alert('Verification link copied to clipboard');
}

export default function ProfilePage() {
  const [certificates, setCertificates] = useState<CertificateListItem[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);

  const [signupName, setSignupName]         = useState('');
  const [certDisplayName, setCertDisplayName] = useState('');
  const [nameLoading, setNameLoading]       = useState(true);
  const [nameSaving, setNameSaving]         = useState(false);
  const [nameSaved, setNameSaved]           = useState(false);
  const [nameError, setNameError]           = useState<string | null>(null);

  useEffect(() => {
    apiFetch<CertificateListItem[]>('/users/me/certificates')
      .then(c => { setCertificates(c); setLoading(false); })
      .catch(e => { setError((e as Error).message); setLoading(false); });
  }, []);

  useEffect(() => {
    apiFetch<CertDisplayNameResponse>('/users/me/cert-display-name')
      .then(r => {
        setSignupName(r.name);
        setCertDisplayName(r.cert_display_name ?? r.name);
        setNameLoading(false);
      })
      .catch(e => { setNameError((e as Error).message); setNameLoading(false); });
  }, []);

  async function saveCertDisplayName() {
    setNameSaving(true);
    setNameError(null);
    setNameSaved(false);
    try {
      await apiFetch('/users/me/cert-display-name', {
        method: 'PATCH',
        body: JSON.stringify({ cert_display_name: certDisplayName }),
      });
      setNameSaved(true);
    } catch (e) {
      setNameError((e as Error).message);
    } finally {
      setNameSaving(false);
    }
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-6)',
  };

  return (
    <main style={{ flex: 1, padding: 'var(--space-8)', maxWidth: 1100, margin: '0 auto', width: '100%' }}>
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
          Profile
        </h1>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
          Your BluePrint SDLC Certificates
        </p>
      </div>

      <div style={{ ...cardStyle, marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
          Name as it should appear on your certificates
        </h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 12px' }}>
          Defaults to your account name ({signupName || '…'}). Confirm or correct it any
          time before you complete a project — this only affects certificates issued
          after you save.
        </p>
        {nameLoading ? (
          <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', fontSize: 14 }}>Loading…</p>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={certDisplayName}
              onChange={e => { setCertDisplayName(e.target.value); setNameSaved(false); }}
              style={{
                padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
                background: 'var(--surface)', color: 'var(--text)', fontSize: 14, minWidth: 240,
              }}
            />
            <button
              onClick={saveCertDisplayName}
              disabled={nameSaving || !certDisplayName.trim()}
              style={{
                padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
                background: 'var(--primary)', color: 'white', fontSize: 13, fontWeight: 500,
                cursor: nameSaving ? 'default' : 'pointer', opacity: nameSaving ? 0.7 : 1,
              }}
            >
              {nameSaving ? 'Saving…' : 'Save'}
            </button>
            {nameSaved && (
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Saved.</span>
            )}
            {nameError && (
              <span style={{ fontSize: 13, color: 'var(--error)' }}>{nameError}</span>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }}>Loading…</p>
      ) : error ? (
        <p style={{ color: 'var(--error)', fontFamily: 'var(--font-body)' }}>{error}</p>
      ) : certificates.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 'var(--space-12)' }}>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text-secondary)' }}>
            No certificates yet. Complete a project&apos;s full SDLC to earn one.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          {certificates.map(cert => (
            <div key={cert.certificateId} style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
              <div>
                <h3 style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                  {cert.applicationName}
                </h3>
                {cert.institutionName && (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' }}>{cert.institutionName}</p>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
                  Issued {formatDate(cert.issuedAt)}
                  {cert.goLiveDate ? ` · Go-live ${formatDate(cert.goLiveDate)}` : ''}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
                <a
                  href={cert.pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
                    background: 'var(--primary)', color: 'white', fontSize: 13, fontWeight: 500,
                    textDecoration: 'none',
                  }}
                >
                  Download
                </a>
                <button
                  onClick={() => shareCertificate(cert)}
                  style={{
                    padding: '8px 16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
                    background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  Share
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
